import moment from "moment";
import 'moment/locale/es';
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { PiMapPin, PiFunnel } from 'react-icons/pi';
import { Text, Title, Button, Badge } from "rizzui";
import { toast } from "sonner";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import useFetch from "../../../hooks/useFetch";
import { PickupService } from "../../../services/pickup/pickupService";
import { Pickup } from "../../../type/entities/pickup-type";
import cn from "../../../utils/classNames";
import CompletePickupModal from "../components/CompletePickupModal";
import FailPickupModal from "../components/FailPickupModal";
import SignPickupModal from "../components/SignPickupModal";
import CustomSelect from "../../../components/forms/CustomSelect";
import AssignPickupModal from "../components/AssignPickupModal";
import {
    DriverHeader,
    EmptyState,
    ErrorState,
    GlobalMessages,
    LoadingState,
    RecommendedProductsSummary,
    RouteGroup
} from "./components";
import './mobile-styles.css';
import StartDailyPickupsModal from "./components/StartDailyPickupsModal";
import FinishDailyPickupsModal from "./components/FinishDailyPickupsModal";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";

interface DriverMobileViewProps {
    className?: string;
}

interface RouteGroupData {
    routeId: string;
    routeName: string;
    routeDescription?: string;
    pickups: PickupWithNearby[];
    totalDrums: number;
    estimatedTime: string;
}

type AssignedWithNearbyItem = {
    pickup: Pickup;
    nearsPickups?: Pickup[][];
};

type PickupWithNearby = Pickup & {
    nearby?: (Pickup & { distanceKm?: number })[];
};

moment.locale('es');

const DriverMobileView: FC<DriverMobileViewProps> = ({ className }) => {
    // STATES
    const [selectedDate, setSelectedDate] = useState<string>(moment().format('YYYY-MM-DD'));
    const [routeGroups, setRouteGroups] = useState<RouteGroupData[]>([]);
    const [downloadingReceipt, setDownloadingReceipt] = useState<Record<string, boolean>>({});
    const [downloadingTicket, setDownloadingTicket] = useState<Record<string, boolean>>({});
    const [openRoutes, setOpenRoutes] = useState<Record<string, boolean>>({});
    const [openNearby, setOpenNearby] = useState<Record<string, boolean>>({});
    const [isCompleteOpen, setIsCompleteOpen] = useState(false);
    const [isFailOpen, setIsFailOpen] = useState(false);
    const [activePickup, setActivePickup] = useState<Pickup | null>(null);
    const [signOpen, setSignOpen] = useState(false);
    const [signPickupId, setSignPickupId] = useState<string | null>(null);
    
    // Estados para el modal de asignación
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [pickupToAssign, setPickupToAssign] = useState<Pickup | null>(null);
    const [isAssigning, setIsAssigning] = useState(false);
    
    // Nuevos estados para filtros
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        pickupType: [] as string[],
        maxDistance: null as number | null,
        showOnlySuggested: false
    });

    // Opciones para filtros
    const pickupTypeOptions = [
        { value: 'failed', label: 'Fallidas' },
        { value: 'rescheduled', label: 'Reprogramadas' },
        { value: 'expired', label: 'Expiradas' },
        { value: 'planified', label: 'Planificadas' }
    ];

    const distanceOptions = [
        { value: 5, label: '5 km' },
        { value: 10, label: '10 km' },
        { value: 15, label: '15 km' },
        { value: 20, label: '20 km' },
        { value: null, label: 'Sin límite' }
    ];

    // Función para filtrar recogidas
    const filterPickups = useCallback((pickups: PickupWithNearby[]) => {
        return pickups.filter(pickup => {
            // Filtro por tipo de recogida
            if (filters.pickupType.length > 0 && !filters.pickupType.includes(pickup.pickupStatus)) {
                return false;
            }

            // Filtro por distancia (solo para recogidas sugeridas con distancia)
            if (filters.maxDistance !== null && pickup.distanceKm && pickup.distanceKm > filters.maxDistance) {
                return false;
            }

            // Filtro para mostrar solo sugeridas
            if (filters.showOnlySuggested && !pickup.isSuggested) {
                return false;
            }

            return true;
        });
    }, [filters]);

    // Aplicar filtros a los grupos de rutas
    const filteredRouteGroups = useMemo(() => {
        return routeGroups.map(group => ({
            ...group,
            pickups: filterPickups(group.pickups)
        })).filter(group => group.pickups.length > 0);
    }, [routeGroups, filterPickups]);

    // TOGGLE FUNCTIONS
    const toggleRoute = (routeId: string) =>
        setOpenRoutes((p) => ({ ...p, [routeId]: !p[routeId] }));

    const toggleNearby = (pickupId: string) =>
        setOpenNearby((p) => ({ ...p, [pickupId]: !p[pickupId] }));

    // MODAL FUNCTIONS
    const openCompleteModal = (p: Pickup) => {
        setActivePickup(p);
        setIsCompleteOpen(true);
    };

    const openFailModal = (p: Pickup) => {
        setActivePickup(p);
        setIsFailOpen(true);
    };

    const closeFailModal = () => {
        setIsFailOpen(false);
        setActivePickup(null);
        refetch();
    };

    const handleCompletedOk = (pickupId: string) => {
        setSignPickupId(pickupId);
        setSignOpen(true);
    };

    const handleSignPickup = (pickupId: string) => {
        setSignPickupId(pickupId);
        setSignOpen(true);
    };

    // Funciones para manejo de asignación de pickups
    const openAssignModal = (pickup: Pickup) => {
        setPickupToAssign(pickup);
        setIsAssignModalOpen(true);
    };

    const closeAssignModal = () => {
        setIsAssignModalOpen(false);
        setPickupToAssign(null);
        setIsAssigning(false);
    };

    // Función para inicializar una recogida (cambiar estado a 'started_by_driver')
    const initializePickup = async (pickup: Pickup): Promise<boolean> => {
        try {
            if (!user) {
                toast.error('Usuario no autenticado');
                return false;
            }

            // Validar permisos
            if (!userCan('execute', 'pickup')) {
                toast.error('No tienes permisos para inicializar recogidas');
                return false;
            }

            // Validar que la recogida esté en estado planificado
            if (pickup.pickupStatus !== 'planned') {
                toast.warning('La recogida ya ha sido inicializada o no está en estado planificado');
                return false;
            }

            await service.changePickupStatus(pickup.id, 'started_by_driver');

            toast.success('Recogida inicializada correctamente', {
                description: `${pickup.headquarter?.name || 'Cliente'} - Estado: En progreso`
            });

            return true;
        } catch (error: any) {
            console.error('Error al inicializar pickup:', error);
            
            const errorMessage = error.response?.data?.message || 
                               error.message || 
                               'Error desconocido al inicializar la recogida';

            toast.error('Error al inicializar la recogida', {
                description: errorMessage
            });

            return false;
        }
    };

    // Función mejorada para asignar pickup con inicialización automática
    const handleAssignPickup = async (pickup: Pickup, retryCount: number = 0) => {
        const maxRetries = 3;
        const retryDelay = Math.pow(2, retryCount) * 1000; // Incremento exponencial: 1s, 2s, 4s

        // Validar usuario autenticado
        if (!user) {
            toast.error('Usuario no autenticado');
            return;
        }

        // Validar permisos
        if (!userCan('execute', 'pickup')) {
            toast.error('No tienes permisos para asignar recogidas');
            return;
        }
        
        setIsAssigning(true);

        try {
            const assignmentData = {
                pickupId: pickup.id,
                userId: user.id,
                assignedAt: new Date().toISOString(),
                originalTime: pickup.pickupDate?.date ? new Date(pickup.pickupDate.date).toISOString() : new Date().toISOString()
            };

            // Paso 1: Asignar la recogida
            await service.assignPickup(assignmentData);

            toast.success('Recogida asignada correctamente', {
                description: `${pickup.headquarter?.name || 'Cliente'} - ${moment(pickup.pickupDate?.date).format('DD/MM/YYYY HH:mm')}`
            });

            // Paso 2: Inicializar automáticamente la recogida
            const initializationSuccess = await initializePickup(pickup);

            if (initializationSuccess) {
                // Actualizar el estado global después de la inicialización exitosa
                refetch();
            }

            closeAssignModal();
        } catch (error: any) {
            console.error(`Error al asignar pickup (intento ${retryCount + 1}):`, error);
            
            // Determinar si el error es recuperable
            const isRetryableError = 
                error.code === 'NETWORK_ERROR' ||
                error.response?.status >= 500 ||
                error.response?.status === 408 || // Request Timeout
                error.response?.status === 429;   // Too Many Requests

            // Intentar reintento si es un error recuperable y no hemos excedido el máximo
            if (isRetryableError && retryCount < maxRetries) {
                toast.warning(`Error de conexión. Reintentando en ${retryDelay / 1000} segundos...`, {
                    description: `Intento ${retryCount + 1} de ${maxRetries}`
                });

                setTimeout(() => {
                    handleAssignPickup(pickup, retryCount + 1);
                }, retryDelay);
                
                return; // No cerrar el modal ni cambiar el estado de loading
            }

            // Error final o no recuperable
            const errorMessage = error.response?.data?.message || 
                               error.message || 
                               'Error desconocido al asignar la recogida';

            if (retryCount >= maxRetries) {
                toast.error('No se pudo asignar la recogida después de varios intentos', {
                    description: 'Por favor, verifica tu conexión e inténtalo más tarde'
                });
            } else {
                toast.error('Error al asignar la recogida', {
                    description: errorMessage
                });
            }
        } finally {
            // Solo cambiar el estado de loading si no vamos a reintentar
            if (retryCount >= maxRetries || !error || 
                !(error.code === 'NETWORK_ERROR' || 
                  error.response?.status >= 500 || 
                  error.response?.status === 408 || 
                  error.response?.status === 429)) {
                setIsAssigning(false);
            }
        }
    };

    // Función wrapper para el modal (sin parámetros)
    const handleModalConfirm = () => {
        if (pickupToAssign) {
            handleAssignPickup(pickupToAssign);
        }
    };

    // HOOKS
    const { userCan } = usePrivilege();
    const { user } = useSelector((state: RootState) => state.auth);
    const service = new PickupService();

    // FETCH DATA
    const [payload, loading, error, refetch] = useFetch(
        useCallback(async () => {
            if (!userCan('view_assigned', 'pickup')) return { success: true, data: [] };

            const filters = {
                limit: 999,
                page: 1,
                filter_filters: {
                    pickupDate: selectedDate,
                    assignedToCurrentUser: true,
                },
            };

            const res = await service.getDriverAssignedPickups(filters);
            const raw = typeof res?.getResponseData === 'function' ? res.getResponseData() : res;

            return raw;
        }, [selectedDate, userCan])
    );

    // Normalize data to array
    const items = useMemo<AssignedWithNearbyItem[]>(() => {
        if (!payload) return [];
        if (Array.isArray(payload)) return payload as AssignedWithNearbyItem[];
        if (Array.isArray((payload as any).data)) return (payload as any).data as AssignedWithNearbyItem[];
        return [];
    }, [payload]);

    // Extract unique headquarters IDs for recommended products
    const headquarters = useMemo<string[]>(() => {
        const uniqueHeadquarters = new Set<string>();
        items.forEach(({ pickup }) => {
            if (pickup.headquarter?.id) {
                uniqueHeadquarters.add(pickup.headquarter.id);
            }
        });
        return Array.from(uniqueHeadquarters);
    }, [items]);

    // UTILITY FUNCTIONS
    const getFilenameFromDisposition = (disp?: string, fallback = 'archivo.pdf') => {
        if (!disp) return fallback;
        const m = /filename\*=UTF-8''([^;]+)|filename="?([^\";]+)"?/i.exec(disp);
        const fn = decodeURIComponent(m?.[1] || m?.[2] || '');
        return fn || fallback;
    };

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES DESCARGAR UN BLOB EN UN ARCHIVO
     * @EN DOWNLOAD A BLOB AS A FILE
     */
    //-------------------------------------------------------------------------------------------------------------
    const downloadBlob = (blob: Blob, filename: string) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };
    //-------------------------------------------------------------------------------------------------------------



    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES GENERA UN ALBARAN PARA UNA RECOGIDA
     * @EN GENERATES A RECEIPT FOR A PICKUP
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleGenerateReceipt = async (id: string, nameHeadquarter: string) => {
        try {
            const today = moment().format('YYYY-MM-DD');
            setDownloadingReceipt(s => ({ ...s, [id]: true }));
            const res: any = await service.generateReceipt(id);
            const axiosRes = res?.response ?? res;
            const blob: Blob = axiosRes?.data as Blob;
            const filename = getFilenameFromDisposition(axiosRes?.headers?.['content-disposition'], `albaran_${nameHeadquarter}_${today}.pdf`);
            downloadBlob(blob, filename);
        } catch (e) {
            console.error(e);
        } finally {
            setDownloadingReceipt(s => ({ ...s, [id]: false }));
        }
    };
    //-------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES GENERA UN TICKET PARA UNA RECOGIDA
     * @EN GENERATES A TICKET FOR A PICKUP
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleGenerateTicket = async (id: string, nameHeadquarter: string) => {
        try {
            const today = moment().format('YYYY-MM-DD');
            setDownloadingTicket(s => ({ ...s, [id]: true }));
            const res: any = await service.generateTicket(id);
            const axiosRes = res?.response ?? res;
            const blob: Blob = axiosRes?.data as Blob;
            const filename = getFilenameFromDisposition(axiosRes?.headers?.['content-disposition'], `ticket_${nameHeadquarter}_${today}.pdf`);
            downloadBlob(blob, filename);
        } catch (e) {
            console.error(e);
        } finally {
            setDownloadingTicket(s => ({ ...s, [id]: false }));
        }
    };
    //-------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA EL TICKET DE UNA RECOGIDA PARA IMPRIMIR
     * @EN SHOWS THE TICKET OF A PICKUP FOR PRINTING
     */
    //-------------------------------------------------------------------------------------------------------------
    const handlePrintTicket = async (pickupId: string) => {
        try {
            const response = await service.generateTicket(pickupId);
            const fileData = response.getResponseData();
            const blob = new Blob([fileData], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            // Abrir en nueva ventana para imprimir
            const printWindow = window.open(url, '_blank');
            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.print();
                };
            } else {
                toast.error('No se pudo abrir la ventana de impresión');
            }

            // Limpiar la URL después de un tiempo
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 1000);

            toast.success('Ticket abierto para impresión');
        } catch (error: any) {
            toast.error(error.message || 'Error al mostrar el ticket para imprimir');
        }
    };
    //-------------------------------------------------------------------------------------------------------------


    // EFFECTS
    // Group pickups by route and calculate totals
    useEffect(() => {
        if (!items || items.length === 0) {
            setRouteGroups([]);
            return;
        }

        const groupsMap = new Map<string, RouteGroupData>();
        let drumsTotal = 0, filtersTotal = 0, degreasersTotal = 0;

        items.forEach(({ pickup, nearsPickups }) => {
            const nearbyFlat: (Pickup & { distanceKm?: number })[] =
                (nearsPickups ?? [])
                    .flat()
                    .map((p: any) => ({
                        ...p,
                        distanceKm: typeof p.distance_m === 'number'
                            ? +(p.distance_m / 1000).toFixed(1)
                            : undefined,
                    }));

            const base: PickupWithNearby = { ...pickup, nearby: nearbyFlat.length ? nearbyFlat : undefined };

            const routeId = base.route?.id || 'no-route';
            const routeName = base.route?.name || 'Sin Ruta Asignada';
            const routeDescription = base.route?.description || undefined;

            if (!groupsMap.has(routeId)) {
                groupsMap.set(routeId, {
                    routeId,
                    routeName,
                    routeDescription,
                    pickups: [],
                    totalDrums: 0,
                    estimatedTime: '0m',
                });
            }

            const g = groupsMap.get(routeId)!;
            g.pickups.push(base);
            g.totalDrums += base.headquarter?.drumsNumber || 0;

            const d = base.headquarter?.drumsNumber || 0;
            drumsTotal += d;
            filtersTotal += Math.ceil(d * 0.1);
            degreasersTotal += Math.ceil(d * 0.05);
        });

        const groupedValues = Array.from(groupsMap.values()).map((group) => {
            const minutes = group.pickups.length * 75;
            const h = Math.floor(minutes / 60);
            const m = minutes % 60;
            group.estimatedTime = h > 0 ? `${h}h ${m}m` : `${m}m`;
            return group;
        });

        setRouteGroups(groupedValues);
        // setTotalRecommendedProducts({
        //     drums: drumsTotal,
        //     filters: filtersTotal,
        //     degreasers: degreasersTotal,
        // });
    }, [items]);


    // Open routes by default
    useEffect(() => {
        setOpenRoutes(prev => {
            const next = { ...prev };
            routeGroups.forEach(r => {
                if (next[r.routeId] === undefined) next[r.routeId] = true;
            });
            return next;
        });
    }, [routeGroups]);

    // PERMISSION CHECK
    if (!userCan('view_assigned', 'pickup')) {
        return (
            <div className="flex items-center justify-center h-64">
                <Text className="text-gray-500">No tienes permisos para ver las recogidas</Text>
            </div>
        );
    }

    // RENDER
    return (
        <div className={cn(" mx-auto p-4 space-y-6", className)}>
            <div className="space-y-4">
                {/* Header */}
                <DriverHeader
                    selectedDate={selectedDate}
                    onDateChange={setSelectedDate}
                />

                {/* Global Messages */}
                <GlobalMessages />

                {/* Recommended Products Summary */}
                <RecommendedProductsSummary
                    headquarters={headquarters}
                />

                {/* Tarjeta de advertencia para fechas anteriores */}
                <StartDailyPickupsModal 
                    routeGroups={routeGroups} 
                    refetchData={refetch} 
                    selectedDate={selectedDate}
                    showWarningOnly={true}
                />

                {/* Loading State */}
                {loading && <LoadingState />}

                {/* Route Groups */}
                {!loading && routeGroups.length > 0 && (
                    <div className="space-y-4">
                        <div className="flex flex-row justify-between w-full">
                            <Title as="h2" className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <PiMapPin className="h-5 w-5 text-blue-600" />
                                Recogidas del Día ({items?.length || 0})
                                {/* Contador de recogidas sugeridas */}
                                {(() => {
                                    const suggestedCount = routeGroups.reduce((total, route) => 
                                        total + route.pickups.filter(p => p.isSuggested).length, 0
                                    );
                                    return suggestedCount > 0 ? (
                                        <span className="ml-2 px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
                                            {suggestedCount} sugerida{suggestedCount > 1 ? 's' : ''}
                                        </span>
                                    ) : null;
                                })()}
                            </Title>
                            
                            {/* Botón de filtros */}
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowFilters(!showFilters)}
                                className="flex items-center gap-2"
                            >
                                <PiFunnel className="h-4 w-4" />
                                Filtros
                                {(filters.pickupType.length > 0 || filters.maxDistance !== null || filters.showOnlySuggested) && (
                                    <Badge variant="flat" className="bg-blue-100 text-blue-700 text-xs">
                                        {filters.pickupType.length + (filters.maxDistance !== null ? 1 : 0) + (filters.showOnlySuggested ? 1 : 0)}
                                    </Badge>
                                )}
                            </Button>
                            
                            {/* Button to finish pickups, in case they are started */}
                            <FinishDailyPickupsModal routeGroups={routeGroups} refetchData={refetch} />

                            {/* Button to initialize pickups, in case they are not started */}
                            <StartDailyPickupsModal 
                                routeGroups={routeGroups} 
                                refetchData={refetch} 
                                selectedDate={selectedDate}
                            />
                        </div>

                        {/* Panel de filtros */}
                        {showFilters && (
                            <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <CustomSelect
                                        id="pickupType"
                                        label="Tipo de Recogida"
                                        placeholder="Seleccionar tipos"
                                        options={pickupTypeOptions}
                                        value={pickupTypeOptions.filter(option => filters.pickupType.includes(option.value))}
                                        onChange={(selected: any) => 
                                            setFilters(prev => ({
                                                ...prev,
                                                pickupType: selected ? selected.map((item: any) => item.value) : []
                                            }))
                                        }
                                        isMulti={true}
                                        isClearable={true}
                                    />

                                    <CustomSelect
                                        id="maxDistance"
                                        label="Distancia Máxima"
                                        placeholder="Seleccionar distancia"
                                        options={distanceOptions}
                                        value={distanceOptions.find(option => option.value === filters.maxDistance) || null}
                                        onChange={(selected: any) => 
                                            setFilters(prev => ({
                                                ...prev,
                                                maxDistance: selected ? selected.value : null
                                            }))
                                        }
                                        isClearable={true}
                                    />

                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            id="showOnlySuggested"
                                            checked={filters.showOnlySuggested}
                                            onChange={(e) => 
                                                setFilters(prev => ({
                                                    ...prev,
                                                    showOnlySuggested: e.target.checked
                                                }))
                                            }
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <label htmlFor="showOnlySuggested" className="text-sm text-gray-700">
                                            Solo recogidas sugeridas
                                        </label>
                                    </div>
                                </div>

                                <div className="flex justify-end space-x-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setFilters({
                                            pickupType: [],
                                            maxDistance: null,
                                            showOnlySuggested: false
                                        })}
                                    >
                                        Limpiar Filtros
                                    </Button>
                                </div>
                            </div>
                        )}

                        {filteredRouteGroups.map(route => (
                            <RouteGroup
                                key={route.routeId}
                                route={route}
                                isOpen={openRoutes[route.routeId] || false}
                                openNearby={openNearby}
                                downloadingReceipt={downloadingReceipt}
                                downloadingTicket={downloadingTicket}
                                onToggleRoute={toggleRoute}
                                onCompletePickup={openCompleteModal}
                                onFailPickup={openFailModal}
                                onSignPickup={handleSignPickup}
                                onToggleNearby={toggleNearby}
                                onGenerateReceipt={handleGenerateReceipt}
                                onGenerateTicket={handleGenerateTicket}
                                onPrintTicket={handlePrintTicket}
                                onRefresh={refetch}
                                onAssignPickup={openAssignModal}
                            />
                        ))}

                        {filteredRouteGroups.length === 0 && routeGroups.length > 0 && (
                            <div className="text-center py-8">
                                <Text className="text-gray-500">
                                    No hay recogidas que coincidan con los filtros seleccionados
                                </Text>
                            </div>
                        )}
                    </div>
                )}

                {/* Empty State */}
                {!loading && routeGroups.length === 0 && <EmptyState selectedDate={selectedDate} />}

                {/* Error State */}
                {error && <ErrorState error={error} onRetry={refetch} />}
            </div>

            {/* Modals */}
            {activePickup && (
                <CompletePickupModal
                    isOpen={isCompleteOpen}
                    onClose={() => {
                        setIsCompleteOpen(false);
                        setActivePickup(null);
                        refetch();
                    }}
                    pickup={activePickup}
                />
            )}

            {activePickup && (
                <FailPickupModal
                    isOpen={isFailOpen}
                    onClose={closeFailModal}
                    pickup={activePickup}
                />
            )}

            {signPickupId && (
                <SignPickupModal
                    isOpen={signOpen}
                    onClose={() => setSignOpen(false)}
                    pickupId={signPickupId}
                    onSigned={() => {
                        refetch();
                    }}
                />
            )}

            {/* Modal de Asignación de Pickup */}
            {pickupToAssign && (
                <AssignPickupModal
                    isOpen={isAssignModalOpen}
                    onClose={closeAssignModal}
                    pickup={pickupToAssign}
                    onConfirm={handleModalConfirm}
                    isLoading={isAssigning}
                />
            )}
        </div>
    );
};

export default DriverMobileView;